import { formatDate } from "date-fns";
import { useEffect } from "react";

import { ErrorRounded, SendRounded } from "@mui/icons-material";
import { Container, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import CampaignSentAnimation from "@assets/images/campaigns/campaign_sent.gif";

import Overlay from "@components/Overlay/Overlay";

import { useSendCampaignQuery } from "@queries/campaign.query";

import useCampaignEmailStore from "@store/campaignStore";

import theme from "@theme/index";

interface IFailureProperties {
  campaignId: string;
  technologyId: string;
}

const Failure = ({ campaignId, technologyId }: IFailureProperties) => {
  const { mutateAsync: sendCampaign, status } = useSendCampaignQuery();
  const { scheduledDateTime } = useCampaignEmailStore();

  const handleResendCampaign = async () => {
    if (status === "pending") return;
    await sendCampaign({ campaignId, technologyId });
  };

  let title = "Issue Setting Up Campaign";
  let overlayTitle = "Sending Campaign...";
  let message =
    "Don’t worry, no emails were sent or effected. Try resending your Campaign or contact us to resolve the issue.";
  let action = "Resend Campaign";

  if (scheduledDateTime) {
    title = "Issue Scheduling Campaign";
    overlayTitle = "Scheduling Campaign...";
    message =
      "No emails have been scheduled to send. Try rescheduling your Campaign or contact us to help resolve the issue.";
    action = "Schedule Campaign";
  }

  return (
    <Container
      maxWidth="sm"
      sx={{
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        height: "100%",
        justifyContent: "center",
      }}
    >
      <Overlay caption="Please do not reload page." isVisible={status === "pending"} title={overlayTitle} />
      <ErrorRounded
        sx={{
          color: theme.palette.warning.main,
          height: "50px",
          mb: 1,
          width: "50px",
        }}
      />
      <Typography variant="h3">{title}</Typography>
      <p style={{ fontSize: "1.2em", textAlign: "center" }}>{message}</p>
      <Button
        color="primary"
        endIcon={status !== "pending" && <SendRounded />}
        onClick={handleResendCampaign}
        sx={{
          mt: 1,
        }}
        variant="contained"
      >
        {status === "pending" ? <CircularProgress size={24} sx={{ color: theme.palette.common.white }} /> : action}
      </Button>
    </Container>
  );
};

const Success = () => {
  const { scheduledDateTime } = useCampaignEmailStore();

  let message = "Your campaign is now in our sending queue and will begin reaching recipients within the next hour.";

  if (scheduledDateTime) {
    const date = formatDate(new Date(scheduledDateTime), "MM/dd/yyyy");
    const time = formatDate(new Date(scheduledDateTime), "hh:mm a");
    message = `Your campaign has been successfully scheduled and will be sent on ${date} at ${time}.`;
  }

  return (
    <Container
      maxWidth="sm"
      sx={{
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        height: "100%",
        justifyContent: "center",
      }}
    >
      <img alt="Campaign Sent Email Animation" height="200em" src={CampaignSentAnimation} />
      <Typography variant="h3">Campaign {scheduledDateTime ? "Scheduled" : "Queued"}</Typography>
      <p style={{ fontSize: "1.2em", textAlign: "center" }}>{message}</p>
    </Container>
  );
};

interface ISentConfirmationProperties {
  campaignId: string;
  isSuccess: boolean;
  technologyId: string;
}

export default function SentConfirmation({ campaignId, isSuccess, technologyId }: ISentConfirmationProperties) {
  const { setScheduledDateTime, setSendOptions } = useCampaignEmailStore();
  useEffect(() => {
    return () => {
      setScheduledDateTime(null);
      setSendOptions("send");
    };
  }, []);
  return isSuccess ? <Success /> : <Failure campaignId={campaignId} technologyId={technologyId} />;
}
